import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Loading from '../components/Loading';
import Toast from '../components/Toast';
import ErrorToast from '../components/ErrorToast';
import FiltroImoveis from '../components/FiltroImoveis';
import Paginacao from '../components/Paginacao';
import { FaEye } from 'react-icons/fa';
import { Tags } from 'lucide-react';
import BulkEditEtiquetasModal from '../components/BulkEditEtiquetasModal';
import '../styles/Common.css';      // import do CSS unificado
import './Imoveis.css';          // Pequenos ajustes específicos, se necessários

const Imoveis = () => {
  const [filters, setFilters] = useState({});
  const [imoveis, setImoveis] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalItens, setTotalItens] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [selecionados, setSelecionados] = useState([]);
  const [modalEtiquetasAberto, setModalEtiquetasAberto] = useState(false);

  const buscarImoveis = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const resposta = await api.get(
        `/imoveis`,
        {
          params: { ...filters, page, pageSize }
        }
      );
      const { data, total } = resposta.data;
      setImoveis(data);
      setTotalItens(total);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao buscar imóveis.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    buscarImoveis();
  }, [buscarImoveis]);

  const allIdsOnPage = useMemo(() => (Array.isArray(imoveis) ? imoveis.map(i => i.id) : []), [imoveis]);
  const allOnPageSelected = useMemo(() => allIdsOnPage.length > 0 && allIdsOnPage.every(id => selecionados.includes(id)), [allIdsOnPage, selecionados]);
  const toggleSelectAllOnPage = () => {
    setSelecionados(prev => {
      if (allOnPageSelected) {
        return prev.filter(id => !allIdsOnPage.includes(id));
      }
      const set = new Set(prev);
      allIdsOnPage.forEach(id => set.add(id));
      return Array.from(set);
    });
  };
  const toggleSelect = (id) => {
    setSelecionados(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleSearch = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  return (
    <div className="page-container imoveis-container">
      <h1 className="page-title imoveis-title">Consulta de Imóveis</h1>

      <div className="filters-section imoveis-filters">
        <FiltroImoveis onSearch={handleSearch} />
      </div>

      {error && <ErrorToast message={error} onClose={() => setError('')} />}
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage('')} />}

      <div style={{ display: 'flex', marginBottom: 12 }}>
        <button
          className="button-secondary"
          onClick={() => setModalEtiquetasAberto(true)}
          title="Editar Etiquetas em Massa"
          disabled={selecionados.length === 0}
        >
          <Tags size={16} />
          <span style={{ marginLeft: 6 }}>Editar Etiquetas ({selecionados.length})</span>
        </button>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAllOnPage}
                      aria-label="Selecionar todos da página"
                    />
                  </th>
                  <th>Detalhes do Imóvel</th>
                  <th>Situação</th>
                  <th>Proprietários</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {imoveis.map(imovel => (
                  <tr key={imovel.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selecionados.includes(imovel.id)}
                        onChange={() => toggleSelect(imovel.id)}
                        aria-label={`Selecionar imóvel ${imovel.id}`}
                      />
                    </td>
                    <td>
                      <div className="imovel-details">
                        <a href={`/imoveis/${imovel.id}`} target="_blank" rel="noopener noreferrer">
                          <strong>ID:</strong> {imovel.id}
                        </a>
                        <span><strong>Endereço:</strong> {`${imovel.logradouro}, ${imovel.numero} - ${imovel.complemento} - ${imovel.bairro}, CEP: ${imovel.cep}`}</span>
                        {imovel.condominio && <span><strong>Condomínio:</strong> {imovel.condominio.nome}</span>}
                        {(() => {
                          const etiquetas = imovel.etiquetas || (imovel.etiqueta ? [imovel.etiqueta] : []);
                          return etiquetas.length > 0 ? (
                            <div className="etiquetas-list">
                              {etiquetas.map((et) => (
                                <span key={et.id || et.nome} className="etiqueta-chip">{et.nome || String(et)}</span>
                              ))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td>{imovel.status}</td>
                    <td>
                      <ul className="proprietarios-list">
                        {imovel?.proprietario_atual?.id ? (
                          <li key={imovel.proprietario_atual.id}>
                            <Link to={`/proprietarios/${imovel.proprietario_atual.id}`} target="_blank" rel="noopener noreferrer">
                              {imovel.proprietario_atual.nome}
                            </Link>
                          </li>
                        ) : (
                          <li>Nenhum proprietário associado</li>
                        )}
                      </ul>
                    </td>
                    <td className="action-icons">
                      <button
                        className="icon-button"
                        title="Detalhes"
                        onClick={() => window.open(`/imoveis/${imovel.id}`, '_blank')}
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Paginacao
            totalItens={totalItens}
            itensPorPagina={pageSize}
            paginaAtual={page}
            setPaginaAtual={setPage}
          />

          <BulkEditEtiquetasModal
            isOpen={modalEtiquetasAberto}
            onClose={() => setModalEtiquetasAberto(false)}
            selectedIds={selecionados}
            type="imoveis"
            onSuccess={() => {
              setToastMessage('Etiquetas atualizadas com sucesso!');
              setSelecionados([]);
              buscarImoveis();
            }}
          />
        </>
      )}
    </div>
  );
};

export default Imoveis;
